/**
 * CrossSliceSelection - Manages a group of parallel slice planes
 *
 * Represents a selection of cross-slice planes generated from a spline path.
 * Stores parameters like start/end position, spacing, and plane type.
 * This is a key component for managing waffle construction plane groups.
 *
 * Ported from cc.sketchchair.core.CrossSliceSelection.java
 * Simplified for Phase 3 - focuses on plane group management
 */

import * as THREE from 'three';
import { Vec2D } from '../sketch/geometry/Vec2D.js';
import { SliceType } from './SliceType.js';
import { CapType } from './CapType.js';
import { SlicePlane } from './SlicePlane.js';

export class CrossSliceSelection {
    // Re-export enums as static properties for backward compatibility
    static SLICES = SliceType.SLICES;
    static LEG = SliceType.LEG;
    static PLANE_ON_EDGE = SliceType.PLANE_ON_EDGE;
    static SINGLE_SLICE = SliceType.SINGLE_SLICE;
    static SLATSLICES = SliceType.SLATSLICES;
    static PLANE = SliceType.PLANE;

    static CAP_CURVE = CapType.CAP_CURVE;
    static CAP_BUTT = CapType.CAP_BUTT;
    static CAP_INSIDE = CapType.CAP_INSIDE;
    static CAP_ROUND_SQUARE = CapType.CAP_ROUND_SQUARE;

    // Join types (how planes connect)
    static JOIN_CURVE = 1;
    static JOIN_STRAIGHT = 2;

    /**
     * Create a new CrossSliceSelection
     * @param {Object} options - Configuration options
     */
    constructor(options = {}) {
        const {
            path = null,           // SketchShape this selection is based on
            plane = null,          // Parent SlicePlane
            start = 0,             // Start position along path (0 to 1)
            end = 1,               // End position along path (0 to 1)
            spacing = 50,          // Spacing between planes in pixels
            type = SliceType.SLICES,
            parentChair = null     // Parent chair/design
        } = options;

        // Core properties
        this.path = path;
        this.plane = plane;
        this.start = start;
        this.end = end;
        this.spacing = spacing;
        this.type = type;
        this.parentChair = parentChair;

        // Generated planes
        this.planes = []; // Array of SlicePlane objects generated from this selection

        // UI state
        this.editing = false;
        this.selected = false;
        this.mousePercent = 0; // Current mouse position along path (for editing)
        this.clicks = 0;       // Click count for editing workflow

        // Advanced options
        this.cropToCurrentShape = true;      // Constrain planes to shape bounds
        this.flipSide = false;                // Flip plane orientation
        this.tieToLeg = false;                // Tie planes to leg structure
        this.extendLegSliceToTopOfLeg = false;
        this.tiedToPlanes = [];               // Array of planes this is connected to
        this.legSpline = null;                // Leg spline if tied to leg

        // Geometry options
        this.borderX = 0;          // Border padding X
        this.borderY = 0;          // Border padding Y
        this.slatHeight = 0;       // Height for slat slices
        this.teethCount = 5;       // Number of teeth for finger joints
        this.capType = CapType.CAP_CURVE;
        this.joinType = CrossSliceSelection.JOIN_STRAIGHT;
        this.offsetRotation = 0;   // Rotation offset in radians
        this.cornerRadius = 10;    // Corner radius for rounded caps
        this.smooth = false;       // Smooth transitions
        this.generateFlushTops = false;
        this.fingerTolerance = 0.0; // Tolerance for finger joints

        // Flags
        this.destroy = false;
        this.tempSlice = false;
    }

    /**
     * Generate parallel planes based on this selection
     * Creates SlicePlane objects along the path
     * @param {SlicePlaneManager} manager - Plane manager to add planes to
     * @returns {Array<SlicePlane>} Array of generated planes
     */
    generatePlanes(manager) {
        if (!this.path || !this.plane) {
            console.warn('CrossSliceSelection: Missing path or plane');
            return [];
        }

        // Clear existing generated planes
        this.planes.forEach(p => {
            if (manager) {
                manager.removePlane(p);
            }
        });
        this.planes = [];

        // Get total length of path section
        const pathLength = this.path.getLength ? this.path.getLength() : 1;
        const startDist = this.start * pathLength;
        const endDist = this.end * pathLength;
        const sectionLength = Math.abs(endDist - startDist);

        // Calculate number of planes
        if (this.type === SliceType.SINGLE_SLICE) {
            // Just one plane at the current position
            const percent = this.start;
            const planeData = this.createPlaneData(percent);
            if (planeData) {
                const plane = this.createSlicePlane(planeData);
                if (plane && manager) {
                    manager.addPlane(plane);
                    this.planes.push(plane);
                }
            }
        } else if (this.type === SliceType.SLICES ||
                   this.type === SliceType.SLATSLICES) {
            // Multiple parallel planes at spacing intervals
            const numPlanes = Math.max(1, Math.floor(sectionLength / this.spacing));
            const actualSpacing = sectionLength / numPlanes;

            for (let i = 0; i <= numPlanes; i++) {
                const dist = startDist + (i * actualSpacing);
                const percent = dist / pathLength;

                const planeData = this.createPlaneData(percent);
                if (planeData) {
                    const plane = this.createSlicePlane(planeData);
                    if (plane && manager) {
                        manager.addPlane(plane);
                        this.planes.push(plane);
                    }
                }
            }
        }

        console.log(`CrossSliceSelection generated ${this.planes.length} planes`);
        return this.planes;
    }

    /**
     * Create plane data at a specific position along the path
     * @param {number} percent - Position along path (0 to 1)
     * @returns {Object|null} Plane data object or null
     * @private
     */
    createPlaneData(percent) {
        if (!this.path) return null;

        // Get position at this percent
        const pos = this.path.getPos ? this.path.getPos(percent) : null;
        if (!pos) return null;

        // Get perpendicular direction (for plane normal)
        const perpendicular = this.path.getPerpendicular ?
            this.path.getPerpendicular(percent) :
            new Vec2D(0, 1);

        // Convert to 3D (assuming parent plane is XY plane at Z=0)
        // Scale factor matches what we use elsewhere
        const scaleFactor = 0.01;
        const position3D = new THREE.Vector3(
            pos.x * scaleFactor,
            pos.y * scaleFactor,
            0
        );

        const normal3D = new THREE.Vector3(
            perpendicular.x,
            perpendicular.y,
            0
        ).normalize();

        // NOTE: flipSide does NOT flip the plane normal - it only swaps
        // which end of the intersection chord each part is slotted from
        // (see SlicePlaneManager.generateSlots). Flipping the normal here
        // cancelled that swap and made "Flip Slots" a no-op.

        return {
            position: position3D,
            normal: normal3D,
            thickness: this.plane ? this.plane.thickness : 5.0,
            percent: percent
        };
    }

    /**
     * Create an actual SlicePlane from plane data
     * @param {Object} planeData - Plane configuration
     * @returns {SlicePlane} Created plane
     * @private
     */
    createSlicePlane(planeData) {
        const plane = new SlicePlane({
            position: planeData.position,
            normal: planeData.normal,
            thickness: planeData.thickness,
            guide: false,
            selected: false
        });

        // Set reference back to this selection
        plane.crossSliceSelection = this;

        // Copy sketch from parent plane if available
        if (this.plane && this.plane.sketch) {
            plane.setSketch(this.plane.sketch.clone());
        }

        return plane;
    }

    /**
     * Start editing this selection
     */
    edit() {
        this.editing = true;
        this.clicks = 0;
        this.selected = true;

        if (this.plane) {
            this.plane.select();
        }
        if (this.path) {
            this.path.select();
        }

        console.log('CrossSliceSelection: Started editing');
    }

    /**
     * Stop editing this selection
     */
    stopEditing() {
        this.editing = false;
        this.clicks = 0;

        if (this.plane) {
            this.plane.unselect();
        }
        if (this.path) {
            this.path.unselect();
        }

        console.log('CrossSliceSelection: Stopped editing');
    }

    /**
     * Handle mouse press during editing
     */
    mousePressed() {
        if (!this.editing) return;
        this.clicks++;
    }

    /**
     * Handle mouse release during editing
     */
    mouseReleased() {
        if (!this.editing) return;

        // Different click workflows for different types
        if (this.type === SliceType.SINGLE_SLICE) {
            if (this.clicks === 1) {
                this.start = this.mousePercent;
                this.end = this.mousePercent;
                this.stopEditing();
            }
        } else if (this.type === SliceType.SLICES ||
                   this.type === SliceType.SLATSLICES) {
            // Click 1: Set start
            if (this.clicks === 1) {
                this.start = this.mousePercent;
            }
            // Click 2: Set end
            else if (this.clicks === 2) {
                this.end = this.mousePercent;
            }
            // Click 3: Set spacing (distance from end)
            else if (this.clicks === 3) {
                const spacingVal = Math.abs(this.mousePercent - this.end);
                this.spacing = spacingVal * 300; // Scale factor
                this.stopEditing();
            }
        } else if (this.type === SliceType.PLANE) {
            if (this.clicks === 2) {
                this.end = 1;
                this.stopEditing();
            }
        }
    }

    /**
     * Update this selection (called each frame during editing)
     * @param {number} mousePercent - Current mouse position along path (0 to 1)
     */
    update(mousePercent) {
        this.mousePercent = mousePercent;

        if (!this.editing) return;

        // Update based on click state
        if (this.type === SliceType.SINGLE_SLICE) {
            if (this.clicks === 0) {
                this.start = this.mousePercent;
                this.end = this.mousePercent;
            }
        } else if (this.type === SliceType.SLICES ||
                   this.type === SliceType.SLATSLICES) {
            if (this.clicks === 0) {
                this.start = this.mousePercent;
            } else if (this.clicks === 1) {
                this.end = this.mousePercent;
            } else if (this.clicks === 2) {
                const spacingVal = Math.abs(this.mousePercent - this.end);
                this.spacing = Math.max(10, spacingVal * 300);
            }
        }
    }

    /**
     * Select this cross-slice selection
     */
    select() {
        this.selected = true;
        if (this.plane) {
            this.plane.select();
        }
    }

    /**
     * Unselect this cross-slice selection
     */
    unselect() {
        this.selected = false;
        this.editing = false;
        if (this.plane) {
            this.plane.unselect();
        }
        this.planes.forEach(p => {
            if (p.unselect) p.unselect();
        });
    }

    /**
     * Clone this cross-slice selection
     * @returns {CrossSliceSelection} Cloned selection
     */
    clone() {
        const cloned = new CrossSliceSelection({
            path: this.path,
            plane: this.plane,
            start: this.start,
            end: this.end,
            spacing: this.spacing,
            type: this.type,
            parentChair: this.parentChair
        });

        // Copy all properties
        cloned.cropToCurrentShape = this.cropToCurrentShape;
        cloned.flipSide = this.flipSide;
        cloned.tieToLeg = this.tieToLeg;
        cloned.extendLegSliceToTopOfLeg = this.extendLegSliceToTopOfLeg;
        cloned.borderX = this.borderX;
        cloned.borderY = this.borderY;
        cloned.slatHeight = this.slatHeight;
        cloned.teethCount = this.teethCount;
        cloned.capType = this.capType;
        cloned.joinType = this.joinType;
        cloned.offsetRotation = this.offsetRotation;
        cloned.cornerRadius = this.cornerRadius;
        cloned.smooth = this.smooth;
        cloned.generateFlushTops = this.generateFlushTops;
        cloned.fingerTolerance = this.fingerTolerance;

        return cloned;
    }

    /**
     * Export to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        return {
            start: this.start,
            end: this.end,
            spacing: this.spacing,
            type: this.type,
            cropToCurrentShape: this.cropToCurrentShape,
            shapeIndex: this.shapeIndex,
            flipSide: this.flipSide,
            tieToLeg: this.tieToLeg,
            extendLegSliceToTopOfLeg: this.extendLegSliceToTopOfLeg,
            legIndex: this.legIndex,
            crossbrace: this.crossbrace,
            crossbraceWidth: this.crossbraceWidth,
            crossbraceBottomOffset: this.crossbraceBottomOffset,
            borderX: this.borderX,
            borderY: this.borderY,
            slatHeight: this.slatHeight,
            teethCount: this.teethCount,
            capType: this.capType,
            joinType: this.joinType,
            offsetRotation: this.offsetRotation,
            cornerRadius: this.cornerRadius,
            smooth: this.smooth,
            generateFlushTops: this.generateFlushTops,
            fingerTolerance: this.fingerTolerance
        };
    }

    /**
     * Load from JSON
     * @param {Object} data - JSON data
     * @param {SketchShape} path - Path shape
     * @param {SlicePlane} plane - Parent plane
     * @returns {CrossSliceSelection} New instance
     */
    static fromJSON(data, path = null, plane = null) {
        const selection = new CrossSliceSelection({
            path: path,
            plane: plane,
            start: data.start,
            end: data.end,
            spacing: data.spacing,
            type: data.type
        });

        // Restore all properties
        if (data.cropToCurrentShape !== undefined) selection.cropToCurrentShape = data.cropToCurrentShape;
        if (data.shapeIndex !== undefined) selection.shapeIndex = data.shapeIndex;
        if (data.flipSide !== undefined) selection.flipSide = data.flipSide;
        if (data.tieToLeg !== undefined) selection.tieToLeg = data.tieToLeg;
        if (data.extendLegSliceToTopOfLeg !== undefined) selection.extendLegSliceToTopOfLeg = data.extendLegSliceToTopOfLeg;
        if (data.legIndex !== undefined) selection.legIndex = data.legIndex;
        if (data.crossbrace !== undefined) selection.crossbrace = data.crossbrace;
        if (data.crossbraceWidth !== undefined) selection.crossbraceWidth = data.crossbraceWidth;
        if (data.crossbraceBottomOffset !== undefined) selection.crossbraceBottomOffset = data.crossbraceBottomOffset;
        if (data.borderX !== undefined) selection.borderX = data.borderX;
        if (data.borderY !== undefined) selection.borderY = data.borderY;
        if (data.slatHeight !== undefined) selection.slatHeight = data.slatHeight;
        if (data.teethCount !== undefined) selection.teethCount = data.teethCount;
        if (data.capType !== undefined) selection.capType = data.capType;
        if (data.joinType !== undefined) selection.joinType = data.joinType;
        if (data.offsetRotation !== undefined) selection.offsetRotation = data.offsetRotation;
        if (data.cornerRadius !== undefined) selection.cornerRadius = data.cornerRadius;
        if (data.smooth !== undefined) selection.smooth = data.smooth;
        if (data.generateFlushTops !== undefined) selection.generateFlushTops = data.generateFlushTops;
        if (data.fingerTolerance !== undefined) selection.fingerTolerance = data.fingerTolerance;

        return selection;
    }

    /**
     * Get string representation
     * @returns {string} Description
     */
    toString() {
        return `CrossSliceSelection[type=${this.type}, start=${this.start.toFixed(2)}, end=${this.end.toFixed(2)}, spacing=${this.spacing.toFixed(1)}, planes=${this.planes.length}]`;
    }
}
